import { useDeleteConfirmation, useServices, useToast } from '@/app/providers';
import { AsyncSearchableSelect, DataTable, type ColumnDef, type SelectOption } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { useCrudNotifications, useDateFormatter, usePaginatedList } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import type { CreateRfidCredentialDTO, RfidCardType, RfidCredential, RfidCredentialStatus, User } from '@/shared/types';
import type { QueryParams } from '@yawi/domain';
import { AlertTriangle, Ban, CheckCircle, CreditCard, Edit2, Plus, Save, Trash2, X, XCircle } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { RfidReaderPanel } from './RfidReaderPanel';

// Helper to mask tag ID
const maskTagId = (tagId: string) => {
  if (tagId.length <= 4) return tagId;
  return '****' + tagId.slice(-4);
};

// Card type labels
const CARD_TYPE_LABELS: Record<RfidCardType, string> = {
  PROXIMITY: 'Proximidad (125kHz)',
  MIFARE: 'MIFARE (13.56MHz)',
  HID: 'HID iClass/Prox',
  EM4100: 'EM4100',
  OTHER: 'Otro',
};

interface RfidFilters {
  status: RfidCredentialStatus | '';
}

// Status badge colors
const getStatusBadge = (status: RfidCredentialStatus) => {
  switch (status) {
    case 'ACTIVE':
      return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle, label: 'Activa' };
    case 'INACTIVE':
      return { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: XCircle, label: 'Inactiva' };
    case 'LOST':
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'Perdida' };
    case 'REVOKED':
      return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: Ban, label: 'Revocada' };
    default:
      return { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: XCircle, label: status };
  }
};

export const AdminRfidList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const { formatDate } = useDateFormatter();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { rfidCredentialRepo, userRepo } = useServices();

  if (!ability.can('manage', 'all') && !ability.can('manage', 'RfidCredential') && !ability.can('read', 'RfidCredential')) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        No tienes permisos para ver esta sección.
      </div>
    );
  }

  const canManageRfidCredential = ability.can('manage', 'all') || ability.can('manage', 'RfidCredential');
  const canCreateRfidCredential = canManageRfidCredential || ability.can('create', 'RfidCredential');
  const canUpdateRfidCredential = canManageRfidCredential || ability.can('update', 'RfidCredential');
  const canDeleteRfidCredential = canManageRfidCredential || ability.can('delete', 'RfidCredential');
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Tarjeta RFID',
  });

  const {
    data: credentials,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList<RfidCredential, RfidFilters>({
    fetchFn: (params) => rfidCredentialRepo.getCredentials(params),
    initialFilters: { status: '' },
    errorTitle: 'Error al cargar tarjetas RFID',
  });

  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<Partial<RfidCredential & { memberId: string }>>({});
  const [isEditMode, setIsEditMode] = useState(false);

  const loadUserOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const params: QueryParams = { page: 1, limit: 10 };
    if (inputValue) params.search = inputValue;
    const response = await userRepo.getUsers(params);
    return response.data.map(u => ({ value: u.id, label: `${u.name} - ${u.email}` }));
  }, [userRepo]);

  const handleOpenCreate = () => {
    if (!canCreateRfidCredential) return;
    setEditingCredential({
      tagId: '',
      memberId: '',
      label: '',
      cardType: 'PROXIMITY',
      facilityCode: '',
      notes: '',
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (credential: RfidCredential) => {
    if (!canUpdateRfidCredential) return;
    setEditingCredential({ ...credential });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, tagId: string) => {
    if (!canDeleteRfidCredential) return;
    await confirmDelete({
      itemName: `Tarjeta ${maskTagId(tagId)}`,
      itemType: 'Tarjeta RFID',
      onConfirm: async () => {
        await withDeleteNotification(() => rfidCredentialRepo.deleteCredential(id));
        refresh();
      }
    });
  };

  const handleChangeStatus = async (id: string, newStatus: RfidCredentialStatus) => {
    if (!canUpdateRfidCredential) return;
    try {
      await withUpdateNotification(() => rfidCredentialRepo.updateStatus(id, newStatus));
      refresh();
    } catch {
      // Error shown by hook
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCredential.tagId || !editingCredential.memberId || !editingCredential.label) return;
    if (isEditMode && !canUpdateRfidCredential) return;
    if (!isEditMode && !canCreateRfidCredential) return;

    try {
      if (isEditMode && editingCredential.id) {
        await withUpdateNotification(() =>
          rfidCredentialRepo.updateCredential(editingCredential.id!, {
            memberId: editingCredential.memberId || undefined,
            label: editingCredential.label,
            notes: editingCredential.notes || undefined,
          })
        );
      } else {
        const dto: CreateRfidCredentialDTO = {
          memberId: editingCredential.memberId!,
          tagId: editingCredential.tagId!,
          label: editingCredential.label!,
          cardType: editingCredential.cardType || 'PROXIMITY',
          facilityCode: editingCredential.facilityCode || undefined,
          notes: editingCredential.notes || undefined,
        };
        await withCreateNotification(() => rfidCredentialRepo.createCredential(dto));
      }
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error shown by hook
    }
  };

  const columns: ColumnDef<RfidCredential>[] = useMemo(() => [
    {
      id: 'card',
      header: 'Tarjeta',
      cell: (cred) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
            <CreditCard size={20} />
          </div>
          <div>
            <p className="font-mono font-bold text-slate-800">{maskTagId(cred.tagId)}</p>
            <p className="text-xs text-slate-500">{cred.label}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'member',
      header: 'Miembro',
      cell: (cred) => (
        <span className="text-slate-700">{cred.member.displayName || 'Sin asignar'}</span>
      ),
    },
    {
      id: 'cardType',
      header: 'Tipo',
      cell: (cred) => (
        <span className="text-sm text-slate-600">
          {CARD_TYPE_LABELS[cred.cardType] || cred.cardType}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (cred) => {
        const badge = getStatusBadge(cred.status);
        const Icon = badge.icon;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${badge.bg} ${badge.text} ${badge.border}`}>
            <Icon size={12} />
            {badge.label}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Creada',
      cell: (cred) => (
        <span className="text-sm text-slate-500">
          {formatDate(cred.createdAt)}
        </span>
      ),
    },
  ], [formatDate]);

  return (
    <>
      <DataTable
        data={credentials}
        columns={columns}
        keyExtractor={(cred) => cred.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Tarjetas RFID"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por tarjeta o miembro..."
        loading={loading}
        emptyMessage="No hay tarjetas RFID registradas."
        actions={
          <div className="flex items-center gap-3">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value as RfidFilters['status'] })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="ACTIVE">Activas</option>
              <option value="INACTIVE">Inactivas</option>
              <option value="LOST">Perdidas</option>
              <option value="REVOKED">Revocadas</option>
            </select>
            {canCreateRfidCredential && (
              <Button onClick={handleOpenCreate} icon={<Plus size={16}/>}>
                Nueva Tarjeta
              </Button>
            )}
          </div>
        }
        rowActions={(canUpdateRfidCredential || canDeleteRfidCredential) ? (cred) => (
          <div className="flex justify-end gap-1">
            {cred.status === 'ACTIVE' && canUpdateRfidCredential && (
              <button
                onClick={() => handleChangeStatus(cred.id, 'INACTIVE')}
                className="p-1.5 text-slate-400 hover:text-amber-600 rounded"
                title="Desactivar"
              >
                <XCircle size={16} />
              </button>
            )}
            {cred.status === 'INACTIVE' && canUpdateRfidCredential && (
              <button
                onClick={() => handleChangeStatus(cred.id, 'ACTIVE')}
                className="p-1.5 text-slate-400 hover:text-green-600 rounded"
                title="Activar"
              >
                <CheckCircle size={16} />
              </button>
            )}
            {(cred.status === 'ACTIVE' || cred.status === 'INACTIVE') && canUpdateRfidCredential && (
              <button
                onClick={() => handleChangeStatus(cred.id, 'REVOKED')}
                className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                title="Revocar"
              >
                <Ban size={16} />
              </button>
            )}
            {canUpdateRfidCredential && (
              <button onClick={() => handleOpenEdit(cred)} className="p-1.5 text-slate-400 hover:text-primary-600 rounded">
                <Edit2 size={16} />
              </button>
            )}
            {canDeleteRfidCredential && (
              <button onClick={() => handleDelete(cred.id, cred.tagId)} className="p-1.5 text-slate-400 hover:text-red-600 rounded">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ) : undefined}
      />

      {/* Create/Edit Modal */}
      {isModalOpen && ((isEditMode && canUpdateRfidCredential) || (!isEditMode && canCreateRfidCredential)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar Tarjeta RFID' : 'Nueva Tarjeta RFID'}
              </h3>
              <button onClick={() => setIsModalOpen(false)}>
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              {/* Panel de lectora RFID - solo en modo crear */}
              {!isEditMode && (
                <RfidReaderPanel
                  onCardRead={(uid) => {
                    setEditingCredential({ ...editingCredential, tagId: uid });
                    toast.success('Código de tarjeta capturado');
                  }}
                  className="mb-2"
                />
              )}

              <Input
                label="Tag ID / UID de Tarjeta *"
                value={editingCredential.tagId || ''}
                onChange={(e) => setEditingCredential({ ...editingCredential, tagId: e.target.value.toUpperCase() })}
                placeholder="Ej. 04A1B2C3D4E5F6"
                required
                disabled={isEditMode}
              />

              <AsyncSearchableSelect
                label="Miembro / Residente *"
                loadOptions={loadUserOptions}
                value={editingCredential.memberId || null}
                onChange={(value) => setEditingCredential({ ...editingCredential, memberId: value ?? '' })}
                placeholder="Buscar miembro..."
                isClearable
                initialOption={
                  editingCredential.memberId
                    ? { value: editingCredential.memberId, label: editingCredential.member?.displayName || 'Miembro asignado' }
                    : null
                }
                defaultOptions
                minLength={0}
              />

              <Input
                label="Etiqueta / Label *"
                value={editingCredential.label || ''}
                onChange={(e) => setEditingCredential({ ...editingCredential, label: e.target.value })}
                placeholder="Ej. Tarjeta principal, Copia de respaldo"
                required
              />

              {!isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Tipo de Tarjeta
                  </label>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                    value={editingCredential.cardType || 'PROXIMITY'}
                    onChange={(e) => setEditingCredential({ ...editingCredential, cardType: e.target.value as RfidCardType })}
                  >
                    <option value="PROXIMITY">Proximidad (125kHz)</option>
                    <option value="MIFARE">MIFARE (13.56MHz / NFC)</option>
                    <option value="HID">HID iClass/Prox</option>
                    <option value="EM4100">EM4100</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </div>
              )}

              {/* Facility Code - solo para tarjetas HID */}
              {!isEditMode && editingCredential.cardType === 'HID' && (
                <Input
                  label="Código de Instalación (Facility Code)"
                  value={editingCredential.facilityCode || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, facilityCode: e.target.value })}
                  placeholder="Ej. 12345"
                />
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Notas (opcional)
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm resize-none"
                  rows={2}
                  value={editingCredential.notes || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, notes: e.target.value })}
                  placeholder="Ej. Tarjeta entregada el 1 de diciembre"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16} />}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
